import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

const ToastContext = createContext(null);

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ({ title, message = "", type = "success", duration = 3500 }) => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setToasts((current) => [...current, { id, title, message, type }]);
      window.setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      success: (title, message) => showToast({ title, message, type: "success" }),
      error: (title, message) => showToast({ title, message, type: "error", duration: 5200 }),
      info: (title, message) => showToast({ title, message, type: "info" }),
      dismiss,
    }),
    [dismiss, showToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-20 z-[9999] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-3">
        {toasts.map((toast) => {
          const Icon = icons[toast.type] || Info;
          const tone =
            toast.type === "error"
              ? "border-red-500/40 bg-red-950/95 text-red-50"
              : toast.type === "info"
                ? "border-white/15 bg-zinc-950/95 text-white"
                : "border-emerald-500/35 bg-zinc-950/95 text-white";
          return (
            <div
              key={toast.id}
              className={`rounded-lg border p-4 shadow-2xl backdrop-blur ${tone}`}
            >
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 shrink-0" size={18} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold">{toast.title}</p>
                  {toast.message && <p className="mt-1 text-sm text-white/70">{toast.message}</p>}
                </div>
                <button
                  onClick={() => dismiss(toast.id)}
                  className="rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white"
                  aria-label="Fermer"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
